import keyMirror from 'keymirror';

// ============================================================================
// View Modes
// ============================================================================

export const VIEW_MODES = {
	REGULAR: 'regular',
	PREVIEW: 'preview',
	REPORT: 'report',
	REPLAY: 'replay',
} as const;

export type ViewMode = (typeof VIEW_MODES)[keyof typeof VIEW_MODES] | null;

// ============================================================================
// Action Types
// ============================================================================

export const playlistActionTypes = keyMirror({
	// Playlist fetching
	GET_PLAYLIST_REQUEST: null,
	GET_PLAYLIST_SUCCESS: null,
	GET_PLAYLIST_ERROR: null,

	GET_PLAYLIST_REPORT_REQUEST: null,
	GET_PLAYLIST_REPORT_SUCCESS: null,
	GET_PLAYLIST_REPORT_ERROR: null,

	GET_PLAYLIST_REPLAY_REQUEST: null,
	GET_PLAYLIST_REPLAY_SUCCESS: null,
	GET_PLAYLIST_REPLAY_ERROR: null,

	// Question explanations
	GET_QUESTIONS_EXPLANATIONS: null,
	GET_QUESTIONS_EXPLANATIONS_SUCCESS: null,

	// Answer submission
	SEND_ANSWER_REQUEST: null,
	SEND_ANSWER_SUCCESS: null,
	SEND_ANSWER_ERROR: null,

	// Playlist completion
	FINISH_PLAYLIST_REQUEST: null,
	FINISH_PLAYLIST_SUCCESS: null,
	FINISH_PLAYLIST_ERROR: null,

	// State management
	CLEAR_STATE: null,
	SET_EXERCISE_SCORE: null,
	SET_CURRENT_QUESTION: null,
	SET_VIEW_MODE: null,
	SET_SKIP_ANSWER_REQUESTS: null,
	SET_SKIP_FINISH_REQUEST: null,
	SET_IS_BRIDGE_PLAYLIST: null,
	SET_IS_PERSONALITY_QUIZ: null,
	ADD_PERSONALITY_QUIZ_ANSWER_ID: null,

	// Exercise types
	HANDLE_NEXT_EXERCISE_TYPES: null,

	// Scoring
	COUNT_AS_CORRECT: null,
	COUNT_AS_INCORRECT: null,
	SET_CORRECT_STREAK: null,

	// Instructions
	TOGGLE_TOPIC_INSTRUCTION: null,
	TOGGLE_QUESTION_INSTRUCTION: null,
	SET_VIEWED_QUESTION_INSTRUCTION: null,
	SET_SHOW_INSTRUCTION_HINT: null,
	SEND_TOPIC_INSTRUCTION_FEEDBACK: null,
	SEND_QUESTION_INSTRUCTION_FEEDBACK: null,

	// Audio
	TOGGLE_INSTRUCTION_AUDIO: null,
	SET_INSTRUCTION_AUDIO_ON: null,
	SAVE_AUDIO_PREFERENCES_REQUEST: null,
	SAVE_AUDIO_PREFERENCES_SUCCESS: null,
	SAVE_AUDIO_PREFERENCES_ERROR: null,

	// Reading mode
	TOGGLE_READING_MODE: null,
	TOGGLE_READING_TITLE: null,
	TOGGLE_PULSATING_HIGHLIGHT: null,
	SET_READING_PROGRESS: null,
	SET_READING_TEXT_SIZE: null,

	// Time tracking
	STORE_USER_QUESTION_TIME: null,

	// Completion states
	SET_FINISH_EXERCISE: null,
	SET_ENDSCREEN: null,
	SET_LEVEL_UP: null,

	// Mistake intervention
	SET_MISTAKE_INTERVENTION: null,
	SEND_MISTAKE_INTERVENTION_REQUEST: null,
	SEND_MISTAKE_INTERVENTION_SUCCESS: null,
	SEND_MISTAKE_INTERVENTION_ERROR: null,
	RESET_MISTAKE_INTERVENTION_REQUEST: null,
	RESET_MISTAKE_INTERVENTION_SUCCESS: null,
	RESET_MISTAKE_INTERVENTION_ERROR: null,

	// WebSocket
	RECEIVED_LAST_ACTIVE_DEVICE_CHANNEL_STREAM: null,

	// AI Tutor
	SEND_AI_TUTOR_FEEDBACK: null,
	TOGGLE_AI_TUTOR_FEEDBACK: null,

	// Game-specific
	SET_CURRENT_LEVEL: null,
});

// Legacy action types for backward compatibility
export const exerciseActionTypes = playlistActionTypes;
export const gameActionTypes = playlistActionTypes;

// ============================================================================
// Game Types
// ============================================================================

export const GAME_TYPES = {
	LANE_CHOOSER_GAME: 'CanoeGame',
	LETTER_PARTY_GAME: 'LetterpartyGame',
	TIME_GAME: 'TimeGame',
};

// ============================================================================
// Exercise Constants
// ============================================================================

export const DEFAULT_SPEECH_LOCALE = 'nl';

export const MAX_ANSWER_TIME_IN_SECONDS = 300;

export const MAX_READING_TEXT_SIZE = 3;

export const MIN_QUESTION_LENGTH_FOR_FAST_ANSWERING = 120;

export const FAST_ANSWERING_TIME_IN_MILLISECONDS = 2000;

export const DESKTOP_READING_HEADER_HEIGHT = 72;

export const MOBILE_READING_HEADER_HEIGHT = 48;

export const STOP_AUDIO_PLAYING_EVENT = 'stopAudioPlaying';

export const DELAY_FOR_INSTRUCTION_HINT_IN_SECONDS = 20;

export const WRONG_QUESTIONS_FOR_SECOND_CHANCE = 3;

export const ACTIVE_CLASS = 'active';

export const VISIBLE_CLASS = 'visible';

export const TRUNCATE_CLASS = 'truncated-text';

export const MIN_VISIBLE_CORRECT_STREAK = 5;

export const ENDSCREEN_TYPES = {
	FULL_SCORE: 'full_score',
	LEVEL_UP: 'level_up',
};
